import React, { useState } from 'react';
import { Table, Button, Row, Col, Form } from 'react-bootstrap';
import { FaEdit, FaPlus, FaTrash, FaDownload } from 'react-icons/fa';
import { Link, useParams } from 'react-router-dom';
import Loader from '../../components/Loader';
import Message from '../../components/Message';
import Paginate from '../../components/Paginate';
import {
  useGetStoreIncomesQuery,
  useDeleteStoreIncomeMutation,
  useCreateStoreIncomeMutation,
} from '../../slices/storeIncomesApiSlice';
import { toast } from 'react-toastify';
import JSZip from 'jszip';
import { saveAs } from 'file-saver';
import * as XLSX from 'xlsx';

const NetStoreCash = () => {
  const { pageNumber } = useParams() || 1;

  const { data, isLoading, error, refetch } = useGetStoreIncomesQuery({ pageNumber });
  const [deleteStoreIncome, { isLoading: loadingDelete }] = useDeleteStoreIncomeMutation();
  const [createStoreIncome, { isLoading: loadingCreate }] = useCreateStoreIncomeMutation();

  const [filter, setFilter] = useState('');
  const [sortField, setSortField] = useState('');
  const [sortOrder, setSortOrder] = useState('asc');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [isDownloading, setIsDownloading] = useState(false);
  const [downloadProgress, setDownloadProgress] = useState(0);

  // Create a new storeIncome
  const createStoreIncomeHandler = async () => {
    if (window.confirm('Are you sure you want to create a new storeIncome?')) {
      try {
        const newStoreIncome = {
          date: new Date().toISOString().split('T')[0],
          account: 'Debit',
          accountName: 'Default Account',
          subAccount: 'Default Sub Account',
          details: 'New StoreIncome',
          amount: 0,
          status: 'Pending',
          category: 'Other',
          notes: 'Default note',
        
        };
        await createStoreIncome(newStoreIncome).unwrap();
        toast.success('New storeIncome created successfully');
        refetch();
      } catch (err) {
        toast.error(err?.data?.message || err.error);
      }
    }
  };

  // Delete an storeIncome
  const deleteHandler = async (id) => {
    if (window.confirm('Are you sure you want to delete this storeIncome?')) {
      try {
        await deleteStoreIncome(id).unwrap();
        toast.success('StoreIncome deleted successfully');
        refetch();
      } catch (err) {
        toast.error(err?.data?.message || err.error);
      }
    }
  };

  // Handle sorting
  const handleSort = (field) => {
    const order = sortField === field && sortOrder === 'asc' ? 'desc' : 'asc';
    setSortField(field);
    setSortOrder(order);
  };

  // Filter and sort storeIncomes
  const filterAndSortStoreIncomes = () => {
    let filteredStoreIncomes = [...(data?.storeIncomes || [])];

    // Filter by text input
    if (filter) {
      filteredStoreIncomes = filteredStoreIncomes.filter(
        (storeIncome) =>
          storeIncome.account?.toLowerCase().includes(filter.toLowerCase()) ||
          storeIncome.accountName?.toLowerCase().includes(filter.toLowerCase()) ||
          storeIncome.details?.toLowerCase().includes(filter.toLowerCase()) ||
          storeIncome.category?.toLowerCase().includes(filter.toLowerCase()) ||
          storeIncome.subAccount?.toLowerCase().includes(filter.toLowerCase()) ||
          storeIncome.notes?.toLowerCase().includes(filter.toLowerCase())
      );
    }

    // Filter by date range
    if (startDate && endDate) {
      const start = new Date(startDate);
      const end = new Date(endDate);
      filteredStoreIncomes = filteredStoreIncomes.filter((storeIncome) => {
        const storeIncomeDate = new Date(storeIncome.date);
        return storeIncomeDate >= start && storeIncomeDate <= end;
      });
    }

    // Sort storeIncomes
    if (sortField) {
      filteredStoreIncomes.sort((a, b) => {
        if (sortField === 'amount') {
          return sortOrder === 'asc' ? a.amount - b.amount : b.amount - a.amount;
        } else if (sortField === 'date') {
          return sortOrder === 'asc'
            ? new Date(a.date) - new Date(b.date)
            : new Date(b.date) - new Date(a.date);
        } else {
          const aValue = a[sortField]?.toLowerCase() || '';
          const bValue = b[sortField]?.toLowerCase() || '';
          return sortOrder === 'asc'
            ? aValue.localeCompare(bValue)
            : bValue.localeCompare(aValue);
        }
      });
    }

    return filteredStoreIncomes;
  };

  // Calculate total for date range
  const calculateTotalForDateRange = () => {
    if (!startDate || !endDate) return 0;
    const start = new Date(startDate);
    const end = new Date(endDate);

    const filteredStoreIncomes = data?.storeIncomes?.filter((storeIncome) => {
      const storeIncomeDate = new Date(storeIncome.date);
      return storeIncomeDate >= start && storeIncomeDate <= end;
    }) || [];

    return filteredStoreIncomes.reduce((acc, storeIncome) => acc + storeIncome.amount, 0);
  };

  // Calculate total for all storeIncomes
  const totalAmount = data?.storeIncomes?.reduce((acc, storeIncome) => acc + storeIncome.amount, 0) || 0;
    
  const downloadStoreIncomeImages = async (storeIncome) => {
  if (!storeIncome.images || storeIncome.images.length === 0) {
    toast.warn("This storeIncome record has no images");
    return;
  }

  const zip = new JSZip();
  const folder = zip.folder(`StoreIncome_${storeIncome._id}`);

  const imagePromises = storeIncome.images.map(async (imagePath) => {
    try {
      const normalized = imagePath.replace(/\\/g, "/");
      const response = await fetch(normalized);
      const blob = await response.blob();

      const filename = normalized.split("/").pop();
      folder.file(filename, blob);
    } catch (error) {
      console.error(`Failed to fetch ${imagePath}`, error);
    }
  });

  await Promise.all(imagePromises);

  zip.generateAsync({ type: "blob" }).then((zipFile) => {
    saveAs(zipFile, `StoreIncome_${storeIncome._id}_Images.zip`);
    toast.success("StoreIncome images downloaded successfully");
  });
};

    const downloadAllStoreIncomeImages = async () => {
  if (!startDate || !endDate) {
    toast.error("Please select a start and end date first");
    return;
  }

  const start = new Date(startDate);
  const end = new Date(endDate);

  // Filter storeIncomes by date range
  const filteredStoreIncomes =
    data?.storeIncomes?.filter((storeIncome) => {
      const storeIncomeDate = new Date(storeIncome.date);
      return storeIncomeDate >= start && storeIncomeDate <= end;
    }) || [];

  // Count images first
  const allImages = filteredStoreIncomes.flatMap((i) => i.images || []);

  if (allImages.length === 0) {
    toast.warn("No storeIncome images found in the selected date range");
    return;
  }

  setIsDownloading(true);
  setDownloadProgress(0);

  const zip = new JSZip();
  const folder = zip.folder("All_StoreIncomes_Images");

  let processed = 0;

  const imagePromises = filteredStoreIncomes.flatMap((storeIncome) =>
    (storeIncome.images || []).map(async (imagePath) => {
      try {
        const normalized = imagePath.replace(/\\/g, "/");
        const response = await fetch(normalized);
        const blob = await response.blob();

        const filename = normalized.split("/").pop();
        folder.file(filename, blob);
      } catch (error) {
        console.error(`Failed to fetch ${imagePath}`, error);
      }

      processed += 1;
      setDownloadProgress(Math.round((processed / allImages.length) * 100));
    })
  );

  await Promise.all(imagePromises);

  zip.generateAsync({ type: "blob" }).then((zipFile) => {
    saveAs(zipFile, "All_StoreIncomes_Images.zip");

    toast.success(
      `Download complete. ${allImages.length} image${allImages.length !== 1 ? "s" : ""} included.`
    );

    setIsDownloading(false);
    setDownloadProgress(0);
  });
};

const exportToExcel = () => {
  const storeIncomes = filterAndSortStoreIncomes();

  const worksheetData = storeIncomes.map((storeIncome) => ({
    Id: storeIncome._id,
    Date: new Date(storeIncome.date).toLocaleDateString(),
    Account: storeIncome.account,
    AccountName: storeIncome.accountName,
    SubAccount: storeIncome.SubAccount,
    Details: storeIncome.details,
    Amount: storeIncome.amount,
    Category: storeIncome.category,
    Notes: storeIncome.notes,
    Images: storeIncome.images[0],
  }));

  const worksheet = XLSX.utils.json_to_sheet(worksheetData);
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, 'StoreIncomes');

  XLSX.writeFile(workbook, 'Cash_Collected.xlsx');
};
  
  return (
    <>
      <Row className="align-items-center">
        <Col>
          <h1>Mini Cash Collected</h1>
        </Col>
        <Col className="text-end">
          
          <Button
  className="my-3 mx-2"
  onClick={downloadAllStoreIncomeImages}
  disabled={isDownloading}
>
  {isDownloading ? (
    <>
      <span className="spinner-border spinner-border-sm me-2"></span>
      Zipping...
    </>
  ) : (
    <>
      <FaDownload /> Download All StoreIncome Images
    </>
  )}
</Button>

{isDownloading && (
  <div className="my-2" style={{ width: "250px" }}>
    <div className="progress" style={{ height: "8px" }}>
      <div
        className="progress-bar progress-bar-striped progress-bar-animated"
        role="progressbar"
        style={{ width: `${downloadProgress}%` }}
      ></div>
    </div>
    <small>{downloadProgress}%</small>
  </div>
      )}

          <Link to='/create/storeIncome' className='btn btn-light my-3'>
            Add Data
          </Link>
          <Button onClick={createStoreIncomeHandler} className="my-3" style={{display: "none"}}>
            <FaPlus /> Add New StoreIncome
          </Button>
          <Button className="my-3 mx-2" onClick={exportToExcel}>
            <FaDownload /> Export to Excel
           </Button>
           <Link to='/update/storeIncome/excelform' className='btn btn-light my-3'>
              BulkUpdate
            </Link>
        </Col>
      </Row>
  
      <Row className="mb-3">
        <Col>
          <h5>Total Amount: ${totalAmount.toFixed(2)}</h5>
        </Col>
        <Col md={4}>
          <Form.Control
            type="text"
            placeholder="Search by account, name, details, category, subAccount, or notes"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
          />
        </Col>
      </Row>
  
      <Row className="mb-3">
        <Col md={4}>
          <Form.Control
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            placeholder="Start Date"
          />
        </Col>
        <Col md={4}>
          <Form.Control
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            placeholder="End Date"
          />
        </Col>
        <Col md={4}>
          <h5>
            Total (Filtered Range): ${calculateTotalForDateRange().toFixed(2)}
          </h5>
        </Col>
      </Row>
  
      {loadingCreate && <Loader />}
      {loadingDelete && <Loader />}
      {isLoading ? (
        <Loader />
      ) : error ? (
        <Message variant="danger">{error?.data?.message || 'An error occurred'}</Message>
      ) : data?.storeIncomes?.length > 0 ? (
        <>
          <Table striped bordered hover responsive className="table-sm">
            <thead>
              <tr>
                <th onClick={() => handleSort('date')} style={{ cursor: 'pointer' }}>
                  Date {sortField === 'date' ? (sortOrder === 'asc' ? '↑' : '↓') : ''}
                </th>
                <th>Account</th>
                <th onClick={() => handleSort('accountName')} style={{ cursor: 'pointer' }}>
                  Account Name {sortField === 'accountName' ? (sortOrder === 'asc' ? '↑' : '↓') : ''}
                </th>
                <th onClick={() => handleSort('details')} style={{ cursor: 'pointer' }}>
                  Details {sortField === 'details' ? (sortOrder === 'asc' ? '↑' : '↓') : ''}
                </th>
                <th onClick={() => handleSort('amount')} style={{ cursor: 'pointer' }}>
                  Amount {sortField === 'amount' ? (sortOrder === 'asc' ? '↑' : '↓') : ''}
                </th>
                <th onClick={() => handleSort('category')} style={{ cursor: 'pointer' }}>
                  Category {sortField === 'category' ? (sortOrder === 'asc' ? '↑' : '↓') : ''}
                </th>
                <th onClick={() => handleSort('notes')} style={{ cursor: 'pointer' }}>
                  Notes {sortField === 'notes' ? (sortOrder === 'asc' ? '↑' : '↓') : ''}
                </th>
                <th>Image</th>
                <th>Actions</th>
                <th>Dld</th>
              </tr>
            </thead>
            <tbody>
              {filterAndSortStoreIncomes().map((storeIncome) => (
                <tr key={storeIncome._id}>
                  <td>{new Date(storeIncome.date).toLocaleDateString()}</td>
                  <td>{storeIncome.account}</td>
                  <td>{storeIncome.accountName}</td>
                  <td>{storeIncome.details}</td>
                  <td>
                    {storeIncome.amount >= 0
                      ? `$${storeIncome.amount.toFixed(2)}`
                      : `-$${Math.abs(storeIncome.amount).toFixed(2)}`}
                  </td>
                  <td>{storeIncome.category}</td>
                  <td>{storeIncome.notes}</td>
                  <td className="thumbnails">
                    {storeIncome.images?.length > 0 && (
                      <img
                        src={storeIncome.images[0]}
                        alt="Thumbnail"
                        className="thumbnail-img me-2"
                        style={{
                          width: '15px',
                          height: '15px',
                          objectFit: 'cover',
                        }}
                      />
                    )}
                  </td>

                  <td>
                    <Button
                      as={Link}
                      to={`/admin/storeIncome/${storeIncome._id}/edit`}
                      variant="light"
                      className="btn-sm mx-2"
                    >
                      <FaEdit />
                    </Button>
                    <Button
                      variant="danger"
                      className="btn-sm"
                      onClick={() => deleteHandler(storeIncome._id)}
                    >
                      <FaTrash />
                    </Button>
                    <Link
                     className="btn btn-primary btn-sm m-2"
                    to={`/storeIncome/${storeIncome._id}`}
                    role="button"
                    >
                       Details
                    </Link>
                    
                    
                    
                  </td>
                  <td>
                   <Button variant="info" className="btn-sm" onClick={() => downloadStoreIncomeImages(storeIncome)}>
                          <FaDownload /> Download Images
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
          <Paginate pages={data.pages} page={data.page} isAdmin={true} type="netcash" />
        </>
      ) : (
        <Message variant="info">No storeIncomes found</Message>
      )}
    </>
  );
};

export default NetStoreCash;